import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Globe, Plus, RefreshCw, Trash2, Settings, Activity, Users,
  DollarSign, Server, Search, Send, Ban, CheckCircle, XCircle,
  ExternalLink, Key, Copy, Eye, EyeOff, Wifi, WifiOff, TrendingUp,
  Clock, AlertTriangle, Megaphone, LayoutDashboard
} from 'lucide-react';
import { getErrorMessage } from '../../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, trend, color = "blue" }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {trend && (
            <p className={`text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend > 0 ? '+' : ''}{trend}% vs last week
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/30`}>
          <Icon className={`w-6 h-6 text-${color}-500`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// App Card Component
const AppCard = ({ app, onRefresh, onSettings, onOpenAdmin, onViewCredentials }) => {
  const isOnline = app.success && app.stats?.health_status === 'online';
  const isPending = app.connection_status === 'pending' || !app.is_connected;
  
  return (
    <Card className={`relative overflow-hidden transition-all hover:shadow-lg ${!isOnline && app.app_id !== 'slidecon' ? 'opacity-70' : ''}`}>
      <div 
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: app.color }}
      />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{app.icon || '📱'}</span>
            <div>
              <CardTitle className="text-lg">{app.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                {app.app_id === 'slidecon' ? (
                  <Badge variant="outline" className="text-green-500 border-green-500 gap-1">
                    <Wifi className="w-3 h-3" /> Online
                  </Badge>
                ) : isPending ? (
                  <Badge variant="outline" className="text-amber-500 border-amber-500 gap-1">
                    <Clock className="w-3 h-3" /> Pending Connection
                  </Badge>
                ) : isOnline ? (
                  <Badge variant="outline" className="text-green-500 border-green-500 gap-1">
                    <Wifi className="w-3 h-3" /> Online
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-red-500 border-red-500 gap-1">
                    <WifiOff className="w-3 h-3" /> Offline
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={() => onRefresh(app.app_id)} data-testid={`refresh-${app.app_id}`}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            {app.app_id !== 'slidecon' && (
              <Button variant="ghost" size="icon" onClick={() => onSettings(app)} data-testid={`settings-${app.app_id}`}>
                <Settings className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {app.stats ? (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{app.stats.total_users?.toLocaleString() || 0}</span>
                <span className="text-muted-foreground">users</span>
              </div>
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{app.stats.total_projects?.toLocaleString() || 0}</span>
                <span className="text-muted-foreground">projects</span>
              </div>
            </div>
            {app.stats.revenue_mtd !== undefined && (
              <div className="flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-green-500" />
                <span className="font-medium">${app.stats.revenue_mtd?.toLocaleString() || 0}</span>
                <span className="text-muted-foreground">this month</span>
              </div>
            )}
          </>
        ) : isPending ? (
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Waiting for app to connect...</p>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full gap-2"
              onClick={() => onViewCredentials(app)}
              data-testid={`view-credentials-${app.app_id}`}
            >
              <Key className="w-4 h-4" />
              View Credentials
            </Button>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {app.error || 'Unable to fetch stats'}
          </div>
        )}
        
        {!isPending && (
          <Button 
            variant="outline" 
            className="w-full mt-2 gap-2"
            onClick={() => onOpenAdmin(app)}
            data-testid={`open-admin-${app.app_id}`}
          >
            <ExternalLink className="w-4 h-4" />
            Open Admin
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminHubManager() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unifiedStats, setUnifiedStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [activity, setActivity] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [credentials, setCredentials] = useState(null);
  
  // Dialog states
  const [addAppOpen, setAddAppOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  
  // Form states
  const [newApp, setNewApp] = useState({ name: '', api_url: '', description: '', icon: '📱', color: '#6366f1' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('email');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: '', message: '', priority: 'normal', app_ids: ['all'] });

  useEffect(() => {
    fetchUnifiedStats();
    fetchActivity();
  }, []);

  const fetchUnifiedStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hub/stats/unified`);
      setUnifiedStats(response.data.totals);
      setApps(response.data.apps);
    } catch (error) {
      toast.error('Failed to fetch hub stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/hub/activity?limit=20`);
      setActivity(response.data.actions);
    } catch (error) {
      console.error('Failed to fetch activity');
    }
  };

  const handleRefreshApp = async (appId) => {
    setRefreshing(true);
    try {
      if (appId === 'slidecon') {
        await fetchUnifiedStats();
      } else {
        const response = await axios.get(`${API}/hub/apps/${appId}/stats?force_refresh=true`);
        setApps(prev => prev.map(a => 
          a.app_id === appId 
            ? { ...a, stats: response.data.stats, success: !response.data.error }
            : a
        ));
      }
      toast.success('Stats refreshed');
    } catch (error) {
      toast.error('Failed to refresh stats');
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddApp = async () => {
    try {
      const response = await axios.post(`${API}/hub/apps`, newApp);
      // Show credentials dialog instead of toast
      setCredentials({
        app_id: response.data.app_id,
        api_key: response.data.api_key,
        secret_id: response.data.secret_id,
        name: newApp.name,
        instructions: response.data.instructions
      });
      setAddAppOpen(false);
      setCredentialsOpen(true);
      setNewApp({ name: '', api_url: '', description: '', icon: '📱', color: '#6366f1' });
      fetchUnifiedStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to register app'));
    }
  };

  const handleViewCredentials = async (app) => {
    try {
      const response = await axios.get(`${API}/hub/apps/${app.app_id}/credentials`);
      setCredentials(response.data);
      setCredentialsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch credentials');
    }
  };

  const handleCopyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleUpdateApp = async () => {
    try {
      await axios.put(`${API}/hub/apps/${selectedApp.app_id}`, {
        name: selectedApp.name,
        description: selectedApp.description,
        api_url: selectedApp.api_url,
        icon: selectedApp.icon,
        color: selectedApp.color,
        is_active: selectedApp.is_active
      });
      toast.success('App updated');
      setSettingsOpen(false);
      fetchUnifiedStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update app'));
    }
  };

  const handleDeleteApp = async () => {
    if (!confirm(`Are you sure you want to remove ${selectedApp.name} from the hub?`)) return;
    
    try {
      await axios.delete(`${API}/hub/apps/${selectedApp.app_id}`);
      toast.success('App removed');
      setSettingsOpen(false);
      fetchUnifiedStats();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove app'));
    }
  };

  const handleRegenerateKey = async () => {
    if (!confirm('Regenerate API key? You will need to update the key in the app.')) return;
    
    try {
      const response = await axios.post(`${API}/hub/apps/${selectedApp.app_id}/regenerate-key`);
      toast.success(
        <div>
          <p>New API Key:</p>
          <p className="text-xs mt-1 font-mono">{response.data.api_key}</p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error('Failed to regenerate key');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.post(`${API}/hub/users/search`, {
        query: searchQuery,
        search_type: searchType
      });
      setSearchResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.info('No users found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleBroadcast = async () => {
    if (!broadcast.title || !broadcast.message) {
      toast.error('Title and message are required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/hub/actions/broadcast`, broadcast);
      toast.success(`Broadcast sent to ${response.data.success_count} app(s)`);
      setBroadcastOpen(false);
      setBroadcast({ title: '', message: '', priority: 'normal', app_ids: ['all'] });
      fetchActivity();
    } catch (error) {
      toast.error('Failed to send broadcast');
    }
  };

  const handleOpenAdmin = (app) => {
    if (app.app_id === 'slidecon') {
      // Already on admin, do nothing or show current stats
      toast.info('You are already on SlideCon admin');
    } else if (app.api_url) {
      // Open external admin in new tab
      window.open(`${app.api_url}/admin`, '_blank');
    }
  };

  const handleBanUser = async (appId, userId) => {
    const reason = prompt('Ban reason:');
    if (!reason) return;
    
    try {
      await axios.post(`${API}/hub/actions/ban-user?app_id=${appId}&user_id=${userId}&reason=${encodeURIComponent(reason)}`);
      toast.success('User banned');
      fetchActivity();
    } catch (error) {
      toast.error('Failed to ban user');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6 text-primary" />
            Central Admin Hub
          </h2>
          <p className="text-sm text-muted-foreground">
            Manage all your apps from one place
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSearchOpen(true)} data-testid="hub-search-btn">
            <Search className="w-4 h-4 mr-2" />
            Search Users
          </Button>
          <Button variant="outline" onClick={() => setBroadcastOpen(true)} data-testid="hub-broadcast-btn">
            <Megaphone className="w-4 h-4 mr-2" />
            Broadcast
          </Button>
          <Button onClick={() => setAddAppOpen(true)} data-testid="hub-add-app-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add App
          </Button>
        </div>
      </div>

      {/* Unified Stats */}
      {unifiedStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard 
            title="Total Users (All Apps)" 
            value={unifiedStats.total_users?.toLocaleString() || 0}
            icon={Users}
            color="blue"
          />
          <StatsCard 
            title="Active Today" 
            value={unifiedStats.active_users_24h?.toLocaleString() || 0}
            icon={Activity}
            color="green"
          />
          <StatsCard 
            title="Total Projects" 
            value={unifiedStats.total_projects?.toLocaleString() || 0}
            icon={LayoutDashboard}
            color="purple"
          />
          <StatsCard 
            title="Apps Status" 
            value={`${unifiedStats.apps_online || 0} Online`}
            icon={Server}
            color="amber"
          />
        </div>
      )}

      <Tabs defaultValue="apps" className="w-full">
        <TabsList>
          <TabsTrigger value="apps">Connected Apps ({apps.length})</TabsTrigger>
          <TabsTrigger value="activity">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="apps" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {apps.map(app => (
              <AppCard
                key={app.app_id}
                app={app}
                onRefresh={handleRefreshApp}
                onSettings={(app) => { setSelectedApp(app); setSettingsOpen(true); }}
                onOpenAdmin={handleOpenAdmin}
                onViewCredentials={handleViewCredentials}
              />
            ))}
            
            {/* Add App Card */}
            <Card 
              className="border-dashed cursor-pointer hover:bg-muted/50 transition-colors flex items-center justify-center min-h-[200px]"
              onClick={() => setAddAppOpen(true)}
            >
              <div className="text-center text-muted-foreground">
                <Plus className="w-8 h-8 mx-auto mb-2" />
                <p>Add Another App</p>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <CardDescription>Actions performed through the hub</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {activity.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No activity yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {activity.map((action) => (
                      <div key={action.action_id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                        <div className={`p-2 rounded-full ${
                          action.status === 'success' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {action.status === 'success' 
                            ? <CheckCircle className="w-4 h-4 text-green-500" />
                            : <XCircle className="w-4 h-4 text-red-500" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            {action.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {action.app_name} • {new Date(action.performed_at).toLocaleString()}
                          </p>
                          {action.target_id && (
                            <p className="text-xs text-muted-foreground">Target: {action.target_id}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add App Dialog */}
      <Dialog open={addAppOpen} onOpenChange={setAddAppOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register New App</DialogTitle>
            <DialogDescription>
              Add an app to manage from this hub. You&apos;ll receive an API key to configure in the app.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-3 space-y-2">
                <Label>App Name</Label>
                <Input 
                  placeholder="My Other App"
                  value={newApp.name}
                  onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
                  data-testid="new-app-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Input 
                  placeholder="📱"
                  value={newApp.icon}
                  onChange={(e) => setNewApp({ ...newApp, icon: e.target.value })}
                  className="text-center text-xl"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>API URL</Label>
              <Input 
                placeholder="https://myapp.emergent.sh"
                value={newApp.api_url}
                onChange={(e) => setNewApp({ ...newApp, api_url: e.target.value })}
                data-testid="new-app-url"
              />
              <p className="text-xs text-muted-foreground">
                The base URL of your app&apos;s API (without /api)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input 
                placeholder="Brief description"
                value={newApp.description}
                onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Brand Color</Label>
              <div className="flex gap-2">
                <Input 
                  type="color"
                  value={newApp.color}
                  onChange={(e) => setNewApp({ ...newApp, color: e.target.value })}
                  className="w-14 h-10 p-1"
                />
                <Input 
                  value={newApp.color}
                  onChange={(e) => setNewApp({ ...newApp, color: e.target.value })}
                  className="flex-1"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddAppOpen(false)}>Cancel</Button>
            <Button onClick={handleAddApp} disabled={!newApp.name || !newApp.api_url} data-testid="register-app-btn">
              Register App
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* App Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>App Settings</DialogTitle>
            <DialogDescription>
              Configure {selectedApp?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3 space-y-2">
                  <Label>App Name</Label>
                  <Input 
                    value={selectedApp.name}
                    onChange={(e) => setSelectedApp({ ...selectedApp, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Input 
                    value={selectedApp.icon || '📱'}
                    onChange={(e) => setSelectedApp({ ...selectedApp, icon: e.target.value })}
                    className="text-center text-xl"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>API URL</Label>
                <Input 
                  value={selectedApp.api_url || ''}
                  onChange={(e) => setSelectedApp({ ...selectedApp, api_url: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Input 
                  value={selectedApp.description || ''}
                  onChange={(e) => setSelectedApp({ ...selectedApp, description: e.target.value })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={selectedApp.is_active !== false}
                  onCheckedChange={(checked) => setSelectedApp({ ...selectedApp, is_active: checked })}
                />
              </div>
              
              <div className="pt-4 border-t space-y-2">
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleRegenerateKey}>
                    <Key className="w-4 h-4 mr-2" />
                    Regenerate Key
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Regenerating the key will require updating the HUB_API_KEY in the app.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <Button variant="destructive" onClick={handleDeleteApp}>
              <Trash2 className="w-4 h-4 mr-2" />
              Remove App
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateApp}>Save Changes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cross-App User Search Dialog */}
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Search Users Across Apps</DialogTitle>
            <DialogDescription>
              Find a user in any connected app
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="user_id">User ID</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
                data-testid="hub-search-input"
              />
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            
            <ScrollArea className="h-[300px]">
              {searchResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {searching ? 'Searching...' : 'Enter a query to search'}
                </p>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((result, i) => (
                    <Card key={i} className="p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{result.user?.name || result.user?.email}</p>
                          <p className="text-sm text-muted-foreground">{result.user?.email}</p>
                          <Badge variant="outline" className="mt-1">{result.app_name}</Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleBanUser(result.app_id, result.user?.user_id)}
                            title="Ban User"
                          >
                            <Ban className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Broadcast</DialogTitle>
            <DialogDescription>
              Send a notification to all users across selected apps
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Target Apps</Label>
              <Select 
                value={broadcast.app_ids[0]} 
                onValueChange={(v) => setBroadcast({ ...broadcast, app_ids: [v] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Apps</SelectItem>
                  <SelectItem value="slidecon">SlideCon Only</SelectItem>
                  {apps.filter(a => a.app_id !== 'slidecon').map(app => (
                    <SelectItem key={app.app_id} value={app.app_id}>{app.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Title</Label>
              <Input 
                placeholder="Announcement title"
                value={broadcast.title}
                onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })}
                data-testid="broadcast-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea 
                placeholder="Your message..."
                value={broadcast.message}
                onChange={(e) => setBroadcast({ ...broadcast, message: e.target.value })}
                rows={4}
                data-testid="broadcast-message"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={broadcast.priority} onValueChange={(v) => setBroadcast({ ...broadcast, priority: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Cancel</Button>
            <Button onClick={handleBroadcast} data-testid="send-broadcast-btn">
              <Send className="w-4 h-4 mr-2" />
              Send Broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* App Credentials Dialog */}
      <Dialog open={credentialsOpen} onOpenChange={setCredentialsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              App Credentials
            </DialogTitle>
            <DialogDescription>
              Use these credentials in your app to connect to the Hub
            </DialogDescription>
          </DialogHeader>
          
          {credentials && (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-amber-800 dark:text-amber-200 text-sm">
                  ⚠️ Save these credentials securely. The Secret ID will only be shown once for new apps.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">App ID</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={credentials.app_id} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleCopyToClipboard(credentials.app_id, 'App ID')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">API Key</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={showApiKey ? credentials.api_key : '••••••••••••••••••••'} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleCopyToClipboard(credentials.api_key, 'API Key')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Secret ID</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={showApiKey ? credentials.secret_id : '••••••••••••••••••••'} 
                      readOnly 
                      className="font-mono text-sm"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleCopyToClipboard(credentials.secret_id, 'Secret ID')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Environment Variables Template */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-sm font-medium">Environment Variables</Label>
                <div className="bg-slate-900 rounded-lg p-3 font-mono text-xs text-green-400 overflow-x-auto">
                  <pre>{`# Add to your app's .env file
HUB_APP_ID=${credentials.app_id}
HUB_API_KEY=${credentials.api_key}
HUB_SECRET_ID=${credentials.secret_id}
HUB_URL=${window.location.origin}`}</pre>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="w-full"
                  onClick={() => handleCopyToClipboard(
                    `HUB_APP_ID=${credentials.app_id}\nHUB_API_KEY=${credentials.api_key}\nHUB_SECRET_ID=${credentials.secret_id}\nHUB_URL=${window.location.origin}`,
                    'Environment variables'
                  )}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Environment Variables
                </Button>
              </div>
              
              {/* Connection Status */}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-sm text-muted-foreground">Connection Status:</span>
                <Badge variant={credentials.is_connected ? "default" : "secondary"}>
                  {credentials.is_connected ? 'Connected' : 'Pending'}
                </Badge>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => { setCredentialsOpen(false); setShowApiKey(false); }}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
